import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useProduct, useProducts } from '../hooks/useQueries';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/ProductCard';
import './ProductDetail.css';
import '../components/RichTextEditor.css';
import DOMPurify from 'dompurify';
import { trackEvent } from '../utils/analytics';

const Stars = ({ rating }) => (
  <span className="stars">
    {[1, 2, 3, 4, 5].map(i => (
      <span key={i} className={`star ${i <= Math.round(rating) ? 'star-filled' : 'star-empty'}`}>★</span>
    ))}
  </span>
);

/* Extracts the video ID from a YouTube embed URL (…/embed/VIDEO_ID?...) */
const getYoutubeId = (url = '') => {
  const match = url.match(/embed\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
};

/* Static thumbnail for the thumbnail rail — cheaper than loading an iframe per thumb */
const getYoutubeThumbnail = (url) => {
  const id = getYoutubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};

/* Full YouTube watch page — tapping the embedded reel opens this in a new tab */
const getYoutubeWatchUrl = (url) => {
  const id = getYoutubeId(url);
  return id ? `https://www.youtube.com/watch?v=${id}` : url;
};

/* Loading skeleton */
const DetailSkeleton = () => (
  <div className="container" style={{ padding: '40px 24px' }}>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48 }}>
      <div style={{ aspectRatio: '1', background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)', borderRadius: 16, backgroundSize: '800px 100%', animation: 'shimmer 1.4s infinite' }} />
      <div>
        {[80, 60, 40, 100, 50, 70].map((w, i) => (
          <div key={i} style={{ height: 16, width: `${w}%`, background: '#f0f0f0', borderRadius: 4, marginBottom: 16 }} />
        ))}
      </div>
    </div>
  </div>
);

/* ── Colour swatch variant group ── */
const ColorVariants = ({ variants, selectedVariant, onSelect }) => {
  const selected = variants[selectedVariant] || {};
  const displayLabel = selected.variant_name || selected.variant_value || '';
  const selectedAdjustment = Number(selected.price_adjustment || 0);

  return (
    <div className="pdp__option-group">
      <p className="pdp__option-label">
        Select Color:&nbsp;
        <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: selected.variant_value,
              border: '1px solid rgba(0,0,0,0.15)',
              verticalAlign: 'middle',
            }}
          />
          {displayLabel}
          {selectedAdjustment > 0 && (
            <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
              (+₹{selectedAdjustment.toLocaleString('en-IN')})
            </span>
          )}
        </strong>
      </p>
      <div className="pdp__variants" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
        {variants.map((v, i) => {
          const adj = Number(v.price_adjustment || 0);
          const soldOut = Boolean(v.is_out_of_stock);
          const title = `${v.variant_name || v.variant_value}${adj > 0 ? ` (+₹${adj.toLocaleString('en-IN')})` : ''}${soldOut ? ' — Sold Out' : ''}`;
          return (
            <button
              key={v.id ?? i}
              title={title}
              className={`pdp__color-swatch ${soldOut ? 'pdp__color-swatch--soldout' : ''}`}
              onClick={() => onSelect(i)}
              style={{
                position: 'relative',
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: v.variant_value,
                border: selectedVariant === i
                  ? '3px solid var(--primary, #333)'
                  : '2px solid rgba(0,0,0,0.15)',
                outline: selectedVariant === i ? '2px solid white' : 'none',
                outlineOffset: selectedVariant === i ? '-5px' : '0',
                cursor: 'pointer',
                padding: 0,
                transition: 'transform 0.15s, border 0.15s',
                transform: selectedVariant === i ? 'scale(1.15)' : 'scale(1)',
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

/* ── Generic text‑button variant group (size, material, etc.) ── */
const TextVariants = ({ label, variants, selectedVariant, onSelect }) => {
  const selectedAdjustment = Number(variants[selectedVariant]?.price_adjustment || 0);
  return (
    <div className="pdp__option-group">
      <p className="pdp__option-label">
        {label}: <strong>{variants[selectedVariant]?.variant_value}</strong>
        {/* {selectedAdjustment > 0 && (
          <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
            {' '}(+₹{selectedAdjustment.toLocaleString('en-IN')})
          </span>
        )} */}
      </p>
      <div className="pdp__variants">
        {variants.map((v, i) => {
          const adj = Number(v.price_adjustment || 0);
          const soldOut = Boolean(v.is_out_of_stock);
          return (
            <button
              key={v.id ?? i}
              className={`pdp__variant ${selectedVariant === i ? 'active' : ''} ${soldOut ? 'pdp__variant--soldout' : ''}`}
              onClick={() => onSelect(i)}
              title={soldOut ? 'Sold Out' : undefined}
            >
              {v.variant_value}
              {/* {adj > 0 && (
                <span style={{ fontSize: 11, marginLeft: 4, opacity: 0.75 }}>
                  (+₹{adj.toLocaleString('en-IN')})
                </span>
              )} */}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ── Customization field renderer ── */
const CustomizationField = ({ field, value, onChange, error }) => {
  const fileInputRef = useRef(null);

  const labelEl = (
    <label
      htmlFor={`cf-${field.id}`}
      className="pdp__option-label"
      style={{ display: 'block', marginBottom: 6 }}
    >
      {field.field_label}
      {field.is_required && <span style={{ color: 'red', marginLeft: 3 }}>*</span>}
    </label>
  );

  const errorEl = error && (
    <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{error}</p>
  );

  switch (field.field_type) {
    case 'text':
      return (
        <div className="pdp__option-group">
          {labelEl}
          <input
            id={`cf-${field.id}`}
            type="text"
            maxLength={field.max_length || undefined}
            value={value || ''}
            onChange={e => onChange(field.id, e.target.value)}
            placeholder={`Enter ${field.field_label}`}
            style={{
              width: '100%',
              padding: '9px 12px',
              border: error ? '1.5px solid #dc2626' : '1.5px solid var(--border, #ddd)',
              borderRadius: 8,
              fontSize: 14,
              fontFamily: 'inherit',
              color: 'var(--text, #333)',
              background: 'var(--surface, #fff)',
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={e => (e.target.style.borderColor = 'var(--primary, #333)')}
            onBlur={e => (e.target.style.borderColor = error ? '#dc2626' : 'var(--border, #ddd)')}
          />
          {errorEl}
        </div>
      );

    case 'select':
      return (
        <div className="pdp__option-group">
          {labelEl}
          <select
            id={`cf-${field.id}`}
            value={value || ''}
            onChange={e => onChange(field.id, e.target.value)}
            style={{
              width: '100%',
              padding: '9px 12px',
              border: error ? '1.5px solid #dc2626' : '1.5px solid var(--border, #ddd)',
              borderRadius: 8,
              fontSize: 14,
              fontFamily: 'inherit',
              color: value ? 'var(--text, #333)' : 'var(--text-muted, #999)',
              background: 'var(--surface, #fff)',
              outline: 'none',
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23666' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
              paddingRight: 36,
            }}
          >
            <option value="">Select {field.field_label}</option>
            {(field.customization_field_options || []).map(opt => (
              <option key={opt.id} value={opt.option_value}>
                {opt.option_label}
                {Number(opt.price_adjustment) > 0 && ` (+₹${opt.price_adjustment})`}
              </option>
            ))}
          </select>
          {errorEl}
        </div>
      );

    case 'checkbox':
      return (
        <div className="pdp__option-group">
          {labelEl}
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 8,
            padding: error ? 8 : 0,
            border: error ? '1.5px solid #dc2626' : 'none',
            borderRadius: error ? 8 : 0,
          }}>
            {(field.customization_field_options || []).map(opt => {
              const checked = Array.isArray(value) ? value.includes(opt.option_value) : false;
              return (
                <label
                  key={opt.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const current = Array.isArray(value) ? value : [];
                      const next = checked
                        ? current.filter(v => v !== opt.option_value)
                        : [...current, opt.option_value];
                      onChange(field.id, next);
                    }}
                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--primary, #333)' }}
                  />
                  {opt.option_label}
                  {Number(opt.price_adjustment) > 0 && (
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>+₹{opt.price_adjustment}</span>
                  )}
                </label>
              );
            })}
          </div>
          {errorEl}
        </div>
      );

    case 'radio':
      return (
        <div className="pdp__option-group">
          {labelEl}
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 8,
            padding: error ? 8 : 0,
            border: error ? '1.5px solid #dc2626' : 'none',
            borderRadius: error ? 8 : 0,
          }}>
            {(field.customization_field_options || []).map(opt => (
              <label
                key={opt.id}
                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}
              >
                <input
                  type="radio"
                  name={`cf-${field.id}`}
                  value={opt.option_value}
                  checked={value === opt.option_value}
                  onChange={() => onChange(field.id, opt.option_value)}
                  style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--primary, #333)' }}
                />
                {opt.option_label}
                {Number(opt.price_adjustment) > 0 && (
                  <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>+₹{opt.price_adjustment}</span>
                )}
              </label>
            ))}
          </div>
          {errorEl}
        </div>
      );

    case 'image': {
      const previewUrl = value instanceof File ? URL.createObjectURL(value) : null;
      return (
        <div className="pdp__option-group">
          {labelEl}
          <input
            ref={fileInputRef}
            id={`cf-${field.id}`}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) onChange(field.id, file);
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                padding: '9px 18px',
                border: error ? '1.5px dashed #dc2626' : '1.5px dashed var(--border, #ddd)',
                borderRadius: 8,
                background: 'var(--surface-alt, #fafafa)',
                cursor: 'pointer',
                fontSize: 13,
                color: error ? '#dc2626' : 'var(--text-muted, #888)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'border-color 0.2s, color 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary, #333)'; e.currentTarget.style.color = 'var(--text, #333)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = error ? '#dc2626' : 'var(--border, #ddd)'; e.currentTarget.style.color = error ? '#dc2626' : 'var(--text-muted, #888)'; }}
            >
              📁 Choose Image
            </button>
            {previewUrl && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border, #ddd)' }}
                />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{value.name}</span>
                <button
                  onClick={() => onChange(field.id, null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16 }}
                  title="Remove"
                >✕</button>
              </div>
            )}
          </div>
          {errorEl}
        </div>
      );
    }

    default:
      return null;
  }
};

/* ────────────────────────────────────────────────── */

/* ── Single expand/collapse row (Shipping / Return / Why Shop With Us) ── */
const AccordionItem = ({ title, html, isOpen, onToggle }) => (
  <div className="pdp__accordion-item">
    <button
      type="button"
      className="pdp__accordion-trigger"
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <span className="pdp__accordion-title">{title}</span>
      <svg
        className={`pdp__accordion-chevron ${isOpen ? 'is-open' : ''}`}
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
      >
        <path d="M2.5 5L7 9.5L11.5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
    {isOpen && (
      <div
        className="pdp__accordion-content rte-content"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    )}
  </div>
);

/* ── Shipping / Return / Why-Shop-With-Us accordion ── */
const InfoAccordion = ({ product }) => {
  const [openKey, setOpenKey] = useState(null);

  const sections = [
    { key: 'shipping', title: 'Shipping Information', raw: product.shipping_info },
    { key: 'return', title: 'Return / Exchange Information', raw: product.return_info },
    { key: 'shop_us', title: 'Why Shop With Us?', raw: product.shop_us },
  ].filter(s => s.raw && String(s.raw).trim() !== '');

  if (sections.length === 0) return null;

  return (
    <div className="pdp__accordion">
      {sections.map(s => (
        <AccordionItem
          key={s.key}
          title={s.title}
          html={DOMPurify.sanitize(s.raw)}
          isOpen={openKey === s.key}
          onToggle={() => setOpenKey(prev => (prev === s.key ? null : s.key))}
        />
      ))}
    </div>
  );
};

/* ────────────────────────────────────────────────── */

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const { data: product, isLoading, isError } = useProduct(id);
  const { data: allProducts = [] } = useProducts();

  const [selectedImg, setSelectedImg] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(0);       // color variant index
  const [selectedOtherVariant, setSelectedOtherVariant] = useState(0); // size/material/etc. index
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState('description');
  const [added, setAdded] = useState(false);
  const [customizations, setCustomizations] = useState({});   // { field_id: value }
  const [customizationErrors, setCustomizationErrors] = useState({}); // { field_id: error message }
  const [giftWrapSelected, setGiftWrapSelected] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkName, setBulkName] = useState('');
  const [bulkMobile, setBulkMobile] = useState('');
  const [bulkQuantity, setBulkQuantity] = useState('');
  const [bulkErrors, setBulkErrors] = useState({});

  useEffect(() => {
    if (!product) return;
    trackEvent('PRODUCT_VIEW', {
      product_id: product.id,
      category_id: product.categoryIds?.[0] ?? null,
    });
  }, [product?.id]);

  if (isLoading) return <DetailSkeleton />;

  if (isError || !product) {
    return (
      <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>😕</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', marginBottom: 12 }}>Product not found</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>Make sure the API server is running.</p>
        <Link to="/products" className="btn btn-primary">Back to All Gifts</Link>
      </div>
    );
  }

  /* ── Derived values ── */
  // useProduct() runs the raw API response through normaliseProduct(),
  // which already builds an ordered { url, isVideo } list as `media`.
  // Fall back to plain image URLs for any caller that skips normalisation.
  const media = product.media?.length
    ? product.media
    : (product.images || []).map((url) => ({ url, isVideo: false }));
  const images = media.map((m) => m.url); // kept for any other legacy reference
  const discount = product.discount
    ?? (product.base_price && product.discount_price
      ? Math.round((1 - product.discount_price / product.base_price) * 100)
      : product.originalPrice && product.price && product.originalPrice > product.price
        ? Math.round((1 - product.price / product.originalPrice) * 100)
        : 0);

  // Group product_variants by variant_type slug
  const rawVariants = product.product_variants || [];
  const colorVariants = rawVariants.filter(v => v.product_variant_type?.slug === 'color');
  const otherVariants = rawVariants.filter(v => v.product_variant_type?.slug !== 'color');

  // Best Seller / New Arrivals badge — same priority + field fallbacks as ProductCard
  const isFeaturedProduct = product.isFeatured ?? Boolean(product.is_featured);
  const isNewArrivalProduct = product.isNewArrival ?? Boolean(product.is_new_arrival);
  const isBulkProduct = product.isBulk ?? Boolean(product.is_bulk);
  const productTag = isFeaturedProduct ? 'Best Seller' : isNewArrivalProduct ? 'New Arrivals' : null;
  const productTagClass = isFeaturedProduct ? 'tag-amber' : isNewArrivalProduct ? 'tag-green' : 'tag-default';

  // Sold-out state of whatever variant combination is currently selected —
  // drives the "Sold Out" badge on the preview image and disables the CTAs.
  // Falls back to the product-level inStock flag when there are no variants.
  const selectedColorVariant = colorVariants[selectedVariant];
  const selectedSizeVariant = otherVariants[selectedOtherVariant];
  const isSelectedSoldOut = colorVariants.length
    ? Boolean(selectedColorVariant?.is_out_of_stock)
    : otherVariants.length
      ? Boolean(selectedSizeVariant?.is_out_of_stock)
      : product.inStock === true;

  // Legacy flat variants array (kept for backward compat)
  const legacyVariants = product.variants || [];

  const customizationFields = product.customization_fields || [];

  // Gift wrapping addon — only shown if slug='gift-wrapping' and is_enabled=true
  const productAddons = product.addons || [];
  const giftWrapAddon = productAddons.find(
    (a) => a.slug === 'gift-wrapping' && a.product_addons?.is_enabled === true
  ) ?? null;

  const giftWrapPrice = giftWrapAddon
    ? Number(giftWrapAddon.product_addons?.custom_price ?? giftWrapAddon.price ?? 0)
    : 0;

  const related = allProducts
    .filter((p) => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  const handleCustomizationChange = (fieldId, val) => {
    setCustomizations(prev => ({ ...prev, [fieldId]: val }));

    // Clear that field's validation error the moment the shopper provides a value
    setCustomizationErrors(prev => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });

    // If this is a select/radio field and the chosen option has a dedicated
    // photo (customOptionId on product.media — stamped in normaliseProduct(),
    // api/index.js), jump the gallery to it — same behaviour as color swatches.
    const field = customizationFields.find((f) => f.id === fieldId);
    if (!field || (field.field_type !== 'select' && field.field_type !== 'radio')) return;

    const opt = (field.customization_field_options || []).find((o) => o.option_value === val);
    if (!opt) return;

    const idx = media.findIndex((m) => m.customOptionId === opt.id);
    if (idx !== -1) setSelectedImg(idx);
  };

  // normaliseProduct() already stamps each media entry with `variantId`
  // (see index.js), so we can match a color variant straight to its image.
  const handleVariantSelect = (i) => {
    setSelectedVariant(i);

    const variant = colorVariants[i];
    if (!variant) return;

    const idx = media.findIndex((m) => m.variantId === variant.id);
    if (idx !== -1) setSelectedImg(idx); // no dedicated image for this variant — keep current preview otherwise
  };

  // Checks every required customization field has a non-empty value.
  // Returns a { field_id: message } map — empty object means everything's valid.
  const validateRequiredCustomizations = () => {
    const errs = {};
    customizationFields.forEach(field => {
      if (!field.is_required) return;
      const val = customizations[field.id];
      const isEmpty = val === undefined || val === null || val === ''
        || (Array.isArray(val) && val.length === 0);
      if (isEmpty) errs[field.id] = `${field.field_label} is required`;
    });
    return errs;
  };

  // Scrolls to and focuses the first field with a validation error
  const focusFirstError = (errs) => {
    const firstFieldId = customizationFields.find(f => errs[f.id])?.id;
    if (firstFieldId == null) return;
    const el = document.getElementById(`cf-${firstFieldId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.focus?.();
  };

  // Build customizations_payload so CartContext receives a ready-to-use array
  // keyed by field.id (guaranteed unique from DB) instead of field_name
  const buildCustomizationsPayload = () =>
    customizationFields
      .filter(field => customizations[field.id] !== undefined && customizations[field.id] !== '')
      .map(field => ({
        field_id: field.id,
        field_name: field.field_name,
        field_label: field.field_label,
        field_type: field.field_type,
        value: customizations[field.id],
      }));

  const handleAddToCart = () => {
    if (isSelectedSoldOut) return;

    const errs = validateRequiredCustomizations();
    if (Object.keys(errs).length > 0) {
      setCustomizationErrors(errs);
      focusFirstError(errs);
      return;
    }

    const colorObj = colorVariants[selectedVariant];
    const colorLabel = colorObj?.variant_name || colorObj?.variant_value || '';

    const otherObj = otherVariants[selectedOtherVariant];
    const otherLabel = otherObj?.variant_value || '';

    const variantLabel = [colorLabel, otherLabel].filter(Boolean).join(' / ') || legacyVariants[selectedVariant] || 'Default';
    addItem(product, variantLabel, qty, buildCustomizationsPayload(), customizationFields, giftWrapSelected ? giftWrapAddon : null);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleBuyNow = () => {
    if (isSelectedSoldOut) return;

    const errs = validateRequiredCustomizations();
    if (Object.keys(errs).length > 0) {
      setCustomizationErrors(errs);
      focusFirstError(errs);
      return;
    }

    const colorObj = colorVariants[selectedVariant];
    const colorLabel = colorObj?.variant_name || colorObj?.variant_value || '';

    const otherObj = otherVariants[selectedOtherVariant];
    const otherLabel = otherObj?.variant_value || '';

    const variantLabel = [colorLabel, otherLabel].filter(Boolean).join(' / ') || legacyVariants[selectedVariant] || 'Default';
    addItem(product, variantLabel, qty, buildCustomizationsPayload(), customizationFields, giftWrapSelected ? giftWrapAddon : null);
    navigate('/checkout');
  };

  /* Rich text fields are stored as HTML (from the admin's rich text editor) — sanitize before rendering */
  const descriptionHtml = DOMPurify.sanitize(product.description || '');
  const shortDescriptionHtml = DOMPurify.sanitize(product.short_description || '');
  const warningTextHtml = DOMPurify.sanitize(product.warning_text || '');

  // ── Variant-wise pricing ──────────────────────────────────────────────
  // Selling price = discount_price if set, else the API's already-discounted
  // `price` field, else base_price as a last resort.
  // Original (pre-discount) price = base_price if set, else the API's
  // `originalPrice` field, else falls back to the selling price itself
  // (meaning: no strike-through shown, since there's no discount).
  // Add the currently selected color's and size's price_adjustment on top so
  // the PDP price updates live as the shopper switches variants —
  // e.g. base ₹650 + Size "4'6\"" (+₹100) = ₹750.
  const baseSellingPrice = Number(product.discount_price ?? product.price ?? product.base_price ?? 0);
  const baseOriginalPrice = Number(product.base_price ?? product.originalPrice ?? baseSellingPrice);
  const colorAdjustment = Number(colorVariants[selectedVariant]?.price_adjustment || 0);
  const sizeAdjustment = Number(otherVariants[selectedOtherVariant]?.price_adjustment || 0);
  const variantAdjustmentTotal = colorAdjustment + sizeAdjustment;

  // ── Customization-wise pricing ──────────────────────────────────────
  // select/radio/checkbox customization options can each carry their own
  // price_adjustment (e.g. Sticker Theme "Super Mario Theme" = +₹50).
  // Sum whatever the shopper currently has selected so the price updates
  // live, same as variants — supports multi-select checkbox fields too.
  const customizationAdjustmentTotal = customizationFields.reduce((sum, field) => {
    if (!['select', 'radio', 'checkbox'].includes(field.field_type)) return sum;
    const val = customizations[field.id];
    if (val === undefined || val === '') return sum;
    const options = field.customization_field_options || [];
    const values = Array.isArray(val) ? val : [val];
    return sum + values.reduce(
      (s, v) => s + Number(options.find((o) => o.option_value === v)?.price_adjustment || 0),
      0
    );
  }, 0);

  const displayPrice = baseSellingPrice + variantAdjustmentTotal + customizationAdjustmentTotal;
  const displayOriginal = baseOriginalPrice + variantAdjustmentTotal + customizationAdjustmentTotal;

  // WhatsApp share — pre-fills product name + this page's URL
  const whatsappNumber = '916355592849'; // country code + number, no + or spaces
  const productName = product.name ?? product.title ?? 'this product';
  const whatsappMessage = `Hi, I'm interested in this product: ${productName}\n${window.location.href}`;
  const whatsappShareUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  // Bulk-order enquiry — same WhatsApp number; message is built from the
  // popup form (name / mobile / quantity) at submit time.
  const buildBulkWhatsappUrl = ({ name, mobile, quantity }) => {
    const msg =
      `Hi, I'm interested in buying "${productName}" in bulk.\n` +
      `Name: ${name}\nMobile: ${mobile}\nQuantity: ${quantity}\n${window.location.href}`;
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(msg)}`;
  };
  const closeBulkModal = () => {
    setBulkModalOpen(false);
    setBulkName('');
    setBulkMobile('');
    setBulkQuantity('');
    setBulkErrors({});
  };
  const handleBulkSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!bulkName.trim()) errs.name = 'Name is required';
    if (!/^\d{10}$/.test(bulkMobile.trim())) errs.mobile = 'Enter a valid 10-digit mobile number';
    if (!bulkQuantity || Number(bulkQuantity) < 1) errs.quantity = 'Enter a valid quantity';
    if (Object.keys(errs).length) {
      setBulkErrors(errs);
      return;
    }
    const url = buildBulkWhatsappUrl({ name: bulkName.trim(), mobile: bulkMobile.trim(), quantity: bulkQuantity });
    window.open(url, '_blank', 'noopener,noreferrer');
    closeBulkModal();
  };

  return (
    <div className="pdp">
      <a
        href={whatsappShareUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="pdp__whatsapp-float"
        aria-label="Chat with us on WhatsApp"
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
          <path d="M12.001 2C6.478 2 2 6.478 2 12c0 1.892.526 3.66 1.438 5.168L2 22l4.958-1.412A9.953 9.953 0 0 0 12.001 22C17.523 22 22 17.523 22 12S17.523 2 12.001 2zm0 18.163a8.14 8.14 0 0 1-4.148-1.133l-.298-.177-3.087.879.883-3.007-.194-.309a8.14 8.14 0 0 1-1.259-4.372c0-4.501 3.663-8.163 8.163-8.163S20.163 7.5 20.163 12.001c0 4.5-3.662 8.162-8.162 8.162z" />
        </svg>
      </a>

      {/* ── Buying Bulk? popup form ── */}
      {bulkModalOpen && (
        <div className="pdp__bulk-modal-backdrop" onClick={closeBulkModal}>
          <div className="pdp__bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdp__bulk-modal__header">
              <h3>Buying in Bulk?</h3>
              <button
                type="button"
                className="pdp__bulk-modal__close"
                onClick={closeBulkModal}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p className="pdp__bulk-modal__subtitle">
              Share your details and we'll get back to you on WhatsApp with bulk pricing for{' '}
              <strong>{productName}</strong>.
            </p>
            <form onSubmit={handleBulkSubmit} noValidate>
              <div className="pdp__bulk-modal__field">
                <label htmlFor="bulk-name">Name</label>
                <input
                  id="bulk-name"
                  type="text"
                  value={bulkName}
                  onChange={(e) => setBulkName(e.target.value)}
                  placeholder="Your name"
                />
                {bulkErrors.name && <span className="pdp__bulk-modal__error">{bulkErrors.name}</span>}
              </div>
              <div className="pdp__bulk-modal__field">
                <label htmlFor="bulk-mobile">Mobile Number</label>
                <input
                  id="bulk-mobile"
                  type="tel"
                  inputMode="numeric"
                  value={bulkMobile}
                  onChange={(e) => setBulkMobile(e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
                  placeholder="10-digit mobile number"
                />
                {bulkErrors.mobile && <span className="pdp__bulk-modal__error">{bulkErrors.mobile}</span>}
              </div>
              <div className="pdp__bulk-modal__field">
                <label htmlFor="bulk-quantity">Quantity</label>
                <input
                  id="bulk-quantity"
                  type="number"
                  min="1"
                  value={bulkQuantity}
                  onChange={(e) => setBulkQuantity(e.target.value)}
                  placeholder="How many do you need?"
                />
                {bulkErrors.quantity && <span className="pdp__bulk-modal__error">{bulkErrors.quantity}</span>}
              </div>
              <button type="submit" className="btn btn-primary pdp__bulk-modal__submit">
                Submit
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="breadcrumb-bar">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link><span>›</span>
            <Link to="/products">All Gifts</Link><span>›</span>
            {product.categoryName && <><Link to={`/products?category=${product.category}`}>{product.categoryName}</Link><span>›</span></>}
            {product.category?.name && <><Link to={`/products?category=${product.category_id}`}>{product.category.name}</Link><span>›</span></>}
            <span>{product.name ?? product.title}</span>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="pdp__layout">
          {/* ── Gallery ── */}
          <div className="pdp__gallery">
            <div className="pdp__thumbnails">
              {media.map((m, i) => (
                <button
                  key={i}
                  className={`pdp__thumb ${selectedImg === i ? 'active' : ''}`}
                  onClick={() => setSelectedImg(i)}
                >
                  <img
                    src={m.isVideo ? (getYoutubeThumbnail(m.url) || m.url) : m.url}
                    alt={`${product.name ?? product.title} ${i + 1}`}
                  />
                  {m.isVideo && <span className="pdp__thumb-play">▶</span>}
                </button>
              ))}
            </div>
            <div className={`pdp__main-img ${media[selectedImg]?.isVideo ? 'is-video' : ''}`}>
              {media[selectedImg]?.isVideo ? (
                <div className="pdp__main-video-wrap">
                  <iframe
                    key={media[selectedImg].url}
                    src={media[selectedImg].url}
                    title={product.name ?? product.title}
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                  />
                  <a
                    className="pdp__video-overlay-link"
                    href={getYoutubeWatchUrl(media[selectedImg].url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Watch on YouTube"
                  >
                    <span className="pdp__video-overlay-badge">▶ Watch on YouTube</span>
                  </a>
                </div>
              ) : (
                <img src={media[selectedImg]?.url || media[0]?.url} alt={product.name ?? product.title} />
              )}
              {isSelectedSoldOut && <span className="pdp__tag tag-soldout">Sold Out</span>}
              {/* {discount > 0 && <span className="pdp__discount">-{discount}%</span>} */}
            </div>
          </div>

          {/* ── Info ── */}
          <div className="pdp__info">
            {(product.categoryName || product.category?.name || product.sku) && (
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 2 }}>
                {(product.categoryName || product.category?.name) && (
                  <Link
                    to={`/products?category=${product.category_id ?? product.category}`}
                    className="pdp__category-link"
                  >
                    {product.categoryName ?? product.category?.name}
                  </Link>
                )}
                {product.sku && (
                  <span style={{ fontSize: 11, color: 'var(--text-light, #b3b3b3)' }}>
                    {(product.categoryName || product.category?.name) && '| '}SKU {product.sku}
                  </span>
                )}
              </div>
            )}

            <div className="pdp__meta">
              <Stars rating={product.rating} />
              <span className="pdp__rating-num">{product.rating}</span>
              <span className="pdp__reviews">({product.reviews} reviews)</span>
            </div>

            {productTag && (
              <span className={`pdp__inline-tag ${productTagClass}`}>{productTag}</span>
            )}

            <div style={{ marginBottom: 16 }}>
              <h1 className="pdp__name">{product.name ?? product.title}</h1>
              {isBulkProduct && (
                <div className="pdp__bulk-link-row">
                  <button
                    type="button"
                    className="pdp__bulk-link"
                    onClick={() => setBulkModalOpen(true)}
                  >
                    Buying Bulk?
                  </button>
                </div>
              )}
              <span
                className="pdp__category-link rte-content"
                dangerouslySetInnerHTML={{ __html: shortDescriptionHtml }}
              />
            </div>

            <div className="pdp__price-row">
              <span className="pdp__price">₹{Number(displayPrice).toLocaleString('en-IN')}</span>
              {displayOriginal > displayPrice && (
                <>
                  <span className="pdp__original">₹{Number(displayOriginal).toLocaleString('en-IN')}</span>
                  <span className="pdp__save">
                    Save ₹{(displayOriginal - displayPrice).toLocaleString('en-IN')} ({discount}% off)
                  </span>
                </>
              )}
            </div>

            {/* ── Out-of-stock: HIDDEN as requested ── */}
            {/* product.inStock === false / product.stock === 0 badge removed */}

            {/* ── Color swatches ── */}
            {colorVariants.length > 0 && (
              <ColorVariants
                variants={colorVariants}
                selectedVariant={selectedVariant}
                onSelect={handleVariantSelect}
              />
            )}

            {/* ── Other structured variants (size, material, etc.) ── */}
            {otherVariants.length > 0 && (
              <TextVariants
                label={otherVariants[0]?.product_variant_type?.name || 'Size'}
                variants={otherVariants}
                selectedVariant={selectedOtherVariant}
                onSelect={setSelectedOtherVariant}
              />
            )}

            {/* ── Customization fields ── */}
            {customizationFields.length > 0 && (
              <div style={{ marginTop: 4 }}>
                {customizationFields.map(field => (
                  <CustomizationField
                    key={field.id}
                    field={field}
                    value={customizations[field.id] ?? ""}
                    onChange={handleCustomizationChange}
                    error={customizationErrors[field.id]}
                  />
                ))}
              </div>
            )}

            {/* ── Gift Wrapping addon ── */}
            {giftWrapAddon && (
              <div className="pdp__option-group">
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '14px 18px',
                    borderRadius: 12,
                    border: giftWrapSelected
                      ? '2px solid var(--primary, #c8956c)'
                      : '1.5px solid var(--border, #e5e7eb)',
                    background: giftWrapSelected
                      ? 'var(--primary-light, #fdf6f0)'
                      : 'var(--bg-warm, #fafaf9)',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, background 0.2s',
                  }}
                >
                  {/* Custom checkbox */}
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 5,
                      border: giftWrapSelected
                        ? '2px solid var(--primary, #c8956c)'
                        : '2px solid var(--border, #ccc)',
                      background: giftWrapSelected ? 'var(--primary, #c8956c)' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.15s',
                    }}
                  >
                    {giftWrapSelected && (
                      <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                        <path d="M1 4.5L4 7.5L10 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <input
                    type="checkbox"
                    checked={giftWrapSelected}
                    onChange={e => setGiftWrapSelected(e.target.checked)}
                    style={{ display: 'none' }}
                  />
                  <span style={{ fontSize: 22, flexShrink: 0 }}>🎀</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text, #333)', margin: 0 }}>
                      Add Gift Wrapping
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--text-muted, #888)', margin: '2px 0 0' }}>
                      Beautifully wrapped with ribbon &amp; a gift tag
                    </p>
                  </div>
                  <span style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: giftWrapPrice === 0 ? '#16a34a' : 'var(--text, #333)',
                    flexShrink: 0,
                  }}>
                    {giftWrapPrice === 0 ? 'FREE' : `+₹${giftWrapPrice.toLocaleString('en-IN')}`}
                  </span>
                </label>
              </div>
            )}

            {/* ── Quantity ── */}
            <div className="pdp__option-group">
              <p className="pdp__option-label">Quantity</p>
              <div className="qty-input">
                <button className="qty-btn" onClick={() => setQty(Math.max(1, qty - 1))}>−</button>
                <span className="qty-num">{qty}</span>
                <button className="qty-btn" onClick={() => setQty(qty + 1)}>+</button>
              </div>
            </div>

            {/* ── Warning notice (only shown if product.warning_text is set) ── */}
            {product.warning_text && (
              <div className="pdp__warning-box">
                <span
                  className="rte-content"
                  dangerouslySetInnerHTML={{ __html: warningTextHtml }}
                />
              </div>
            )}

            {/* ── CTA ── */}
            <div className="pdp__cta">
              <button
                className={`btn btn-primary pdp__add-btn ${added ? 'btn-added' : ''}`}
                onClick={handleAddToCart}
                disabled={isSelectedSoldOut}
              >
                {isSelectedSoldOut ? 'Sold Out' : added ? '✓ Added to Cart!' : '🛒 Add to Cart'}
              </button>
              <button
                className="btn btn-primary-warm pdp__buy-btn"
                onClick={handleBuyNow}
                disabled={isSelectedSoldOut}
              >
                Buy Now
              </button>
            </div>
            <Link to="/cart" className="pdp__cart-link">View Cart →</Link>

            {/* ── Shipping / Return / Why Shop With Us (expand-collapse) ── */}
            <InfoAccordion product={product} />
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="pdp__tabs">
          <div className="pdp__tab-nav">
            {/* , 'reviews' just add in array to show the review tab */}
            {['description'].map((t) => (
              <button
                key={t}
                className={`pdp__tab-btn ${tab === t ? 'active' : ''}`}
                onClick={() => setTab(t)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <div className="pdp__tab-content">
            {tab === 'description' && (
              <div
                className="rte-content"
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            )}
            {tab === 'reviews' && (
              <div className="pdp__reviews">
                <div className="reviews-summary">
                  <div className="reviews-score">
                    <span className="reviews-big-num">{product.rating}</span>
                    <Stars rating={product.rating} />
                    <p>{product.reviews} reviews</p>
                  </div>
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 16 }}>
                  Be the first to leave a review for this product!
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ── Related products ── */}
        {related.length > 0 && (
          <section style={{ marginTop: 60 }}>
            <div className="section-header-row">
              <div>
                <p className="section-label">You May Also Like</p>
                <h2 className="section-title">Related Gifts</h2>
              </div>
            </div>
            <div className="products-grid">
              {related.map((p, i) => <ProductCard key={p.id} product={p} delay={i * 60} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;